<script setup lang="ts">
import { computed, shallowRef, watchEffect } from 'vue'
import { EStates } from '../../composables/useCreateComponentConfig'
import type { ILinkListProps, TLinkListConfig } from './types'
import { defineNuxtLink } from '#app'
import { isDark } from '@/lib/utils'
import { ICON_SIZES, SIZES, type TIconSizes, type TSizes } from '@/ui/ts/types/Sizes'
import useLinkListConfig from '@/ui/plushComponents/VLinkList/composables/useLinkListConfig'

const props = withDefaults(defineProps<ILinkListProps>(), {
    size: SIZES.MEDIUM,
})

const sizeToIconSizeMap: Partial<Record<TSizes, TIconSizes>> = {
    [SIZES.MEDIUM]: ICON_SIZES.SMALL,
    [SIZES.LARGE]: ICON_SIZES.MEDIUM,
}

const is = computed(() => (!props.to && !props.href ? 'button' : defineNuxtLink({})))
const isDisabled = computed(() => props.state === EStates.DISABLED || props.disabled)
const iconSize = computed(() => sizeToIconSizeMap[props.size] || ICON_SIZES.SMALL)

const linkListConfig = shallowRef({} as TLinkListConfig)

watchEffect(() => {
    linkListConfig.value = useLinkListConfig(props)
})

const attributes = computed(() => {
    const baseAttributes = {
        class: [linkListConfig.value.root, isDark(props.dark)],
        disabled: isDisabled.value,
        'aria-disabled': isDisabled.value,
        'data-test': `link-list-${props.dataTest}`,
        'data-gpp-tools-ui': true,
    }

    const linkAttributes = {
        ...baseAttributes,
        to: props.to || props.href,
        target: props.target,
        ...(props.download ? { download: true } : {}),
    }

    const buttonAttributes = {
        ...baseAttributes,
    }

    return is.value === 'button' ? buttonAttributes : linkAttributes
})
</script>

<template>
    <component
        :is="is"
        v-bind="attributes"
    >
        <VIcon
            v-if="props.leadingIcon"
            :name="props.leadingIcon"
            class="block"
            :size="iconSize"
            :dark="props.dark"
        />

        <div class="flex w-full flex-row items-center">
            <div class="flex w-full flex-col gap-1">
                <span :class="linkListConfig.label">
                    {{ props.linkLabel }}
                </span>

                <p :class="linkListConfig.description">
                    <slot name="additionalText" />
                </p>
            </div>

            <VIcon
                v-if="props.trailingIcon && props.hasDivider"
                class="ml-auto"
                :size="ICON_SIZES[props.size]"
                :name="props.trailingIcon"
                :dark="props.dark"
            />
        </div>
    </component>

    <VDivider v-if="props.hasDivider" />
</template>
